import React, { useState } from 'react';
import {
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    TableContainer,
    Select,
    Box,
    MenuItem,
    TextField,
    Card,
    CardContent,
    Grid,
    Chip,
    IconButton,
    InputAdornment,
    Tooltip,
    Pagination,
    FormControl,
    InputLabel,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { useGetInvoicesQuery } from '../../redux/residentPortalApi';
import {
    Add as AddIcon,
    FilterList as FilterListIcon,
    Search as SearchIcon,
    CalendarMonth as CalendarIcon,
    Refresh as RefreshIcon,
    MoreVert as MoreVertIcon,
    AttachMoney as AttachMoneyIcon,
    RequestQuote as RequestQuoteIcon,
} from '@mui/icons-material';
import { formatCurrency } from '../../utils/currencyUtil';

const GenericInvoices: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [filters, setFilters] = useState({
        development: '',
        type: '',
        phase: '',
        fromDate: '2023-12-16',
        toDate: '2025-12-16',
    });
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [order] = useState('dueDate DESC');
    const [showFilters, setShowFilters] = useState(false);

    const { data: invoices, isLoading, refetch } = useGetInvoicesQuery({
        development: filters.development,
        type: filters.type,
        phase: filters.phase,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        isGeneric: true,
        limit: pageSize,
        skip: page * pageSize,
        order,
    });

    const handleFilterChange = (field: string, value: string) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            [field]: value,
        }));
    };

    const handlePageChange = (_event: React.ChangeEvent<unknown>, newPage: number) => {
        setPage(newPage - 1);
    };

    const handlePageSizeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        const newSize = Number(event.target.value);
        setPageSize(newSize);
        setPage(0);
    };

    const clearFilters = () => {
        setFilters({
            development: '',
            type: '',
            phase: '',
            fromDate: '2023-12-16',
            toDate: '2025-12-16',
        });
    };

    const getStatusColor = (invoice: any) => {
        const dueDate = invoice.dueDate ? new Date(invoice.dueDate) : null;
        const today = new Date();

        if (!dueDate) return 'default';

        if (dueDate < today) {
            return 'error';
        }

        const sevenDaysFromNow = new Date();
        sevenDaysFromNow.setDate(today.getDate() + 7);
        if (dueDate <= sevenDaysFromNow) {
            return 'warning';
        }

        return 'success';
    };

    const getStatusText = (invoice: any) => {
        const dueDate = invoice.dueDate ? new Date(invoice.dueDate) : null;
        const today = new Date();

        if (!dueDate) return 'Unknown';

        if (dueDate < today) {
            return 'Overdue';
        }

        const sevenDaysFromNow = new Date();
        sevenDaysFromNow.setDate(today.getDate() + 7);
        if (dueDate <= sevenDaysFromNow) {
            return 'Due Soon';
        }

        return 'Upcoming';
    };

    return (
        <Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
            {/* Header Section */}
            <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 3 }}>
                <CardContent>
                    <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                        <Grid item xs={12} md={6}>
                            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                <RequestQuoteIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                Generic Invoices
                            </Typography>
                            <Typography variant="body1" color="textSecondary" sx={{ mt: 1 }}>
                                Create and manage once-off generic invoices not tied to a specific invoice type
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                            <Link to="/admin/create-generic-invoice" style={{ textDecoration: 'none' }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<AddIcon />}
                                    size={isMobile ? "small" : "medium"}
                                    sx={{
                                        borderRadius: 2,
                                        boxShadow: 2,
                                        px: 3,
                                        py: 1,
                                        mr: 1
                                    }}
                                >
                                    Create Generic Invoice
                                </Button>
                            </Link>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<RefreshIcon />}
                                size={isMobile ? "small" : "medium"}
                                onClick={() => refetch()}
                                sx={{ borderRadius: 2 }}
                            >
                                {isMobile ? '' : 'Refresh'}
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Filters Section */}
            <Card sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent sx={{ pb: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                            <FilterListIcon sx={{ mr: 1 }} />
                            Filters
                        </Typography>
                        <Button
                            color="primary"
                            size="small"
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            {showFilters ? 'Hide Filters' : 'Show Filters'}
                        </Button>
                    </Box>

                    {showFilters && (
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    label="Development"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={filters.development}
                                    onChange={(e) => handleFilterChange('development', e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    label="Type"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={filters.type}
                                    onChange={(e) => handleFilterChange('type', e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    label="Phase"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={filters.phase}
                                    onChange={(e) => handleFilterChange('phase', e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    label="From Date"
                                    type="date"
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    fullWidth
                                    value={filters.fromDate}
                                    onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <CalendarIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    label="To Date"
                                    type="date"
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    fullWidth
                                    value={filters.toDate}
                                    onChange={(e) => handleFilterChange('toDate', e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <CalendarIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                        </Grid>
                    )}

                    {showFilters && (
                        <Box display="flex" justifyContent="flex-end" mb={1}>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={clearFilters}
                                sx={{ mr: 1 }}
                            >
                                Clear Filters
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={() => refetch()}
                            >
                                Apply Filters
                            </Button>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Invoices Table */}
            <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <CardContent sx={{ p: 0 }}>
                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee' }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                            <AttachMoneyIcon sx={{ mr: 1 }} />
                            Generic Invoices
                        </Typography>
                        <Box>
                            <FormControl variant="outlined" size="small" sx={{ minWidth: 100, mr: 1 }}>
                                <InputLabel id="rows-per-page-label">Rows</InputLabel>
                                <Select
                                    labelId="rows-per-page-label"
                                    value={pageSize}
                                    onChange={handlePageSizeChange as any}
                                    label="Rows"
                                >
                                    <MenuItem value={5}>5</MenuItem>
                                    <MenuItem value={10}>10</MenuItem>
                                    <MenuItem value={20}>20</MenuItem>
                                    <MenuItem value={50}>50</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>

                    {isLoading ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography>Loading invoices...</Typography>
                        </Box>
                    ) : !invoices || invoices.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography>No generic invoices found. Try adjusting your filters.</Typography>
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead sx={{ backgroundColor: theme.palette.primary.main }}>
                                    <TableRow>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Development</TableCell>
                                        {!isMobile && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type</TableCell>}
                                        {!isMobile && !isTablet && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Phase</TableCell>}
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Invoice Description</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Due Date</TableCell>
                                        {!isMobile && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>}
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Amount</TableCell>
                                        {!isMobile && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {invoices.map((invoice, index) => (
                                        <TableRow
                                            key={invoice.id}
                                            sx={{
                                                backgroundColor: index % 2 === 0 ? '#f9f9f9' : 'white',
                                                '&:hover': { backgroundColor: '#f1f1f1' },
                                            }}
                                        >
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                    {invoice.development.charAt(0).toUpperCase() + invoice.development.slice(1)}
                                                </Typography>
                                            </TableCell>
                                            {!isMobile && (
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {invoice.type.charAt(0).toUpperCase() + invoice.type.slice(1)}
                                                    </Typography>
                                                </TableCell>
                                            )}
                                            {!isMobile && !isTablet && (
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {invoice.phase.charAt(0).toUpperCase() + invoice.phase.slice(1)}
                                                    </Typography>
                                                </TableCell>
                                            )}
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {invoice.invoiceDescription || 'No description'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {invoice.dueDate ? (
                                                    <Typography variant="body2">
                                                        {new Date(invoice.dueDate).toLocaleDateString('en-US', {
                                                            year: 'numeric',
                                                            month: 'short',
                                                            day: 'numeric',
                                                        })}
                                                    </Typography>
                                                ) : (
                                                    <Typography variant="body2">Not Available</Typography>
                                                )}
                                            </TableCell>
                                            {!isMobile && (
                                                <TableCell>
                                                    <Chip
                                                        label={getStatusText(invoice)}
                                                        color={getStatusColor(invoice)}
                                                        size="small"
                                                        sx={{ fontWeight: 'medium' }}
                                                    />
                                                </TableCell>
                                            )}
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        color: theme.palette.primary.main
                                                    }}
                                                >
                                                    {formatCurrency(invoice.totalCost)}
                                                </Typography>
                                            </TableCell>
                                            {!isMobile && (
                                                <TableCell>
                                                    <Tooltip title="More Actions">
                                                        <IconButton size="small">
                                                            <MoreVertIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            p: 2,
                            borderTop: '1px solid #eee'
                        }}
                    >
                        <Pagination
                            count={Math.ceil((invoices?.length || 0) / pageSize) || 1}
                            page={page + 1}
                            onChange={handlePageChange}
                            color="primary"
                            showFirstButton
                            showLastButton
                        />
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default GenericInvoices;
